/**
 * WAREHOUSE-03 (Seth 2026-08-25): "How do we plan on the warehouse crew to
 * know what to check in pegasus and what is pending to work on... my less
 * than techy warehouse worker [should] quickly open up his phone, find the
 * installs, and find the warehouse tab."
 *
 * This is that answer: ONE page, phone-first, zero training. Every open
 * install with equipment to pull, soonest install date first, with a pull
 * progress bar. Tap a job → its Warehouse battle station (deep link).
 * Jobs with everything pulled sink into a calm "Ready" section.
 */
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Warehouse, Search, Check, CalendarClock, MapPin, ChevronRight } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { cn } from "@/lib/cn";
import { displayCustomerName } from "@/lib/format";
import { PageHeader } from "@/components/ui/PageHeader";
import { SectionTitle } from "@/components/ui/PageHeader";
import { MaterialRequestsQueue } from "./MaterialRequestsQueue";
import { RestockPane } from "./RestockPane";
import { CountsHub } from "./CountsHub";

export interface QueueRow {
  jobId: string;
  customer: string;
  address: string | null;
  installDate: string | null;
  window: string | null;
  total: number;
  pulled: number;
}

/** Exported (FIELD-M1a): the Pegasus Field warehouse Today reads this same
 *  queue. BUG FIX same pass: the select asked pegasus_job for customer_name /
 *  address — columns that DON'T EXIST (title/site_address are real) — so
 *  PostgREST 400'd and the destructure swallowed it: the queue rendered EMPTY.
 *  Names now resolve from mirror_customer like the Jobs list does. */
export function useWarehouseQueue() {
  return useQuery({
    queryKey: ["warehouse_queue"],
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000, // the warehouse leaves this open — keep it live
    queryFn: async (): Promise<QueueRow[]> => {
      const { data: jobs, error } = await supabase.from("pegasus_job")
        .select("id, custno, title, status, site_address, site_city")
        .not("status", "in", "(closed,cancelled)");
      if (error) throw error;
      const raw = (jobs ?? []) as { id: string; custno: string; title: string | null; site_address: string | null; site_city: string | null }[];
      const custnos = [...new Set(raw.map((j) => j.custno))];
      const names = new Map<string, string>();
      if (custnos.length) {
        const { data: cs } = await supabase.from("mirror_customer").select("custno, fullname").in("custno", custnos);
        for (const c of (cs ?? []) as { custno: string; fullname: string | null }[]) {
          if (c.fullname) names.set(c.custno, c.fullname);
        }
      }
      const jobRows = raw.map((j) => ({
        id: j.id,
        custno: j.custno,
        customer_name: displayCustomerName(null, names.get(j.custno) ?? null) || j.title || j.custno,
        address: [j.site_address, j.site_city].filter(Boolean).join(", ") || null,
      }));
      if (jobRows.length === 0) return [];
      const ids = jobRows.map((j) => j.id);
      const [{ data: eq }, { data: vis }] = await Promise.all([
        supabase.from("pegasus_job_equipment").select("job_id, pulled_at").in("job_id", ids),
        supabase.from("pegasus_dispatch").select("job_id, scheduled_date, scheduled_window")
          .in("job_id", ids).is("deleted_at", null),
      ]);
      const eqBy = new Map<string, { total: number; pulled: number }>();
      for (const e of (eq ?? []) as { job_id: string; pulled_at: string | null }[]) {
        const c = eqBy.get(e.job_id) ?? { total: 0, pulled: 0 };
        c.total += 1;
        if (e.pulled_at) c.pulled += 1;
        eqBy.set(e.job_id, c);
      }
      const visBy = new Map<string, { date: string; window: string | null }>();
      for (const v of (vis ?? []) as { job_id: string; scheduled_date: string | null; scheduled_window: string | null }[]) {
        if (!v.scheduled_date) continue;
        const cur = visBy.get(v.job_id);
        if (!cur || v.scheduled_date < cur.date) visBy.set(v.job_id, { date: v.scheduled_date, window: v.scheduled_window });
      }
      return jobRows
        .map((j) => {
          const c = eqBy.get(j.id) ?? { total: 0, pulled: 0 };
          const v = visBy.get(j.id) ?? null;
          return {
            jobId: j.id, customer: j.customer_name ?? j.custno, address: j.address,
            installDate: v?.date ?? null, window: v?.window ?? null,
            total: c.total, pulled: c.pulled,
          };
        })
        .filter((r) => r.total > 0) // nothing scoped = nothing for the warehouse yet
        .sort((a, b) => (a.installDate ?? "9999") < (b.installDate ?? "9999") ? -1 : 1);
    },
  });
}

const dayLabel = (iso: string | null) => {
  if (!iso) return "Not scheduled";
  const d = new Date(`${iso}T12:00:00`);
  const today = new Date(); today.setHours(12, 0, 0, 0);
  const diff = Math.round((d.getTime() - today.getTime()) / 864e5);
  if (diff === 0) return "TODAY";
  if (diff === 1) return "Tomorrow";
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
};

export function WarehouseView() {
  const { data: rows, isLoading } = useWarehouseQueue();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [lens, setLens] = useState<"topull" | "all">("topull");

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (rows ?? []).filter((r) => !s || `${r.customer} ${r.address ?? ""}`.toLowerCase().includes(s));
  }, [rows, q]);
  const toPull = filtered.filter((r) => r.pulled < r.total);
  const ready = filtered.filter((r) => r.pulled >= r.total);
  const shown = lens === "topull" ? toPull : filtered;

  const Row = ({ r }: { r: QueueRow }) => {
    const done = r.pulled >= r.total;
    const today = r.installDate ? dayLabel(r.installDate) === "TODAY" : false;
    return (
      <button type="button" onClick={() => navigate(`/jobs/${r.jobId}?tab=warehouse`)}
        className={cn(
          "flex w-full items-center gap-3.5 rounded-2xl border p-4 text-left transition-colors",
          done ? "border-emerald-200 bg-emerald-50/50" : "border-hairline bg-white shadow-sm hover:bg-slate-50",
        )}>
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-2">
            <span className={cn(
              "rounded-md px-2 py-0.5 text-[13px] font-bold",
              today ? "bg-rose-600 text-white" : r.installDate ? "bg-brand-50 text-brand-700" : "bg-amber-50 text-amber-700",
            )}>
              <CalendarClock className="mr-1 inline h-3.5 w-3.5 align-[-2px]" />
              {dayLabel(r.installDate)}{r.window ? ` · ${r.window}` : ""}
            </span>
          </span>
          <span className="mt-1.5 flex items-center gap-1.5 text-[17px] font-bold leading-snug text-slate-900">
            <MapPin className="h-4 w-4 shrink-0 text-slate-400" />
            <span className="min-w-0 truncate">{r.address ?? r.customer}</span>
          </span>
          {r.address && <span className="mt-0.5 block truncate text-sm text-slate-500">{r.customer}</span>}
          <span className="mt-2 flex items-center gap-2.5">
            <span className="h-2 min-w-0 flex-1 overflow-hidden rounded-full bg-slate-100">
              <span className={cn("block h-full rounded-full transition-all", done ? "bg-emerald-500" : "bg-amber-400")}
                style={{ width: `${r.total ? (r.pulled / r.total) * 100 : 0}%` }} />
            </span>
            <span className={cn("shrink-0 text-sm font-bold tabular-nums", done ? "text-emerald-700" : "text-slate-600")}>
              {done ? <><Check className="mr-0.5 inline h-4 w-4 align-[-2px]" strokeWidth={3} />Ready</> : `${r.pulled}/${r.total} pulled`}
            </span>
          </span>
        </span>
        <ChevronRight className="h-5 w-5 shrink-0 text-slate-300" />
      </button>
    );
  };

  return (
    <div className="mx-auto max-w-3xl p-4 sm:p-6">
      <PageHeader
        icon={Warehouse}
        title="Warehouse"
        description="Every install with equipment to pull — soonest first. Tap a job to open its pull sheet."
      />

      {/* MATERIAL-REQ-01: what the crews asked for from their phones — first, it's what's hot. */}
      <div className="mb-6"><MaterialRequestsQueue /></div>

      {/* WH-RESTOCK-01: the running stock list — walk the warehouse, add lines,
          Make the POs (one draft per vendor). Not tied to any job. */}
      <div className="mb-6"><RestockPane /></div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="flex gap-1 rounded-lg bg-slate-100 p-0.5">
          {([["topull", `To Pull (${toPull.length})`], ["all", `All (${filtered.length})`]] as const).map(([k, label]) => (
            <button key={k} onClick={() => setLens(k)}
              className={cn("rounded-md px-3.5 py-1.5 text-sm font-semibold transition", lens === k ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700")}>
              {label}
            </button>
          ))}
        </div>
        <div className="relative min-w-0 flex-1 sm:max-w-xs">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search address or customer…"
            className="w-full rounded-lg border border-hairline py-2 pl-9 pr-3 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30" />
        </div>
      </div>

      {isLoading ? (
        <p className="rounded-xl border border-hairline bg-white py-10 text-center text-sm text-slate-400">Loading the pull queue…</p>
      ) : shown.length === 0 ? (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50/60 py-10 text-center">
          <Check className="mx-auto h-8 w-8 text-emerald-500" strokeWidth={3} />
          <p className="mt-2 text-[15px] font-bold text-emerald-800">
            {lens === "topull" ? "All caught up — every scoped job is pulled." : "No installs with scoped equipment."}
          </p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {shown.map((r) => <Row key={r.jobId} r={r} />)}
        </div>
      )}

      {lens === "topull" && ready.length > 0 && (
        <div className="mt-6">
          <SectionTitle icon={Check} tone="emerald" title="Ready for the Truck" count={ready.length} className="mb-2" />
          <div className="space-y-2.5">{ready.map((r) => <Row key={r.jobId} r={r} />)}</div>
        </div>
      )}

      {/* WH-RESTOCK-01/02: the count rotations (shelf + every truck) — the
          system remembers, not the GM. */}
      <div className="mt-6"><CountsHub /></div>
    </div>
  );
}
